import {
  ComponentProps,
  FunctionComponent,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import Icon from "@mui/material/Icon";
import { styled } from "@mui/material";
import { useConfiguration } from "../../dataAccess/api/configuration";
import { scrollIntoView } from "../../helpers/navigation";
import { iframe } from "../../helpers/environment";

import { ProfileContext } from "../public/Profile";
import { ThemeButton } from "../ui/ThemeComponents/ThemeButton";
import OnlyDesktop from "../ui/OnlyDesktop";
import OnlyMobile from "../ui/OnlyMobile";
import ProfileAccessModal from "./ProfileAccessModal";

export const profileDataMobileAttribute = "data-mobile-profile-element";
export const profileDataDesktopAttribute = "data-desktop-profile-element";

interface Props {
  children: string;
  className?: string;
  itemSlug?: string;
}

const StyledButton = styled(ThemeButton)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    width: "unset",
  },
}));

const LoginAnchorButton: FunctionComponent<Props> = ({
  children,
  itemSlug,
}: Props) => {
  const { setLoginOpen } = useContext(ProfileContext);
  const mobileAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { configuration } = useConfiguration();
  const onEnd = useCallback(() => {
    if (setLoginOpen) {
      setLoginOpen(true);
    }
  }, [setLoginOpen]);

  const loginToClaimOnClickDesktop = useCallback(() => {
    if (configuration?.ssoSetting.forceJwtAuth) {
      return;
    }
    const element = document.querySelector(`[${profileDataDesktopAttribute}]`);

    if (!element) {
      onEnd();
      return;
    }

    scrollIntoView({
      element,
      onEnd,
    });
  }, [configuration, onEnd]);

  const loginToClaimOnClickMobile = useCallback(() => {
    if (configuration?.ssoSetting.forceJwtAuth) {
      return;
    }
    setMobileOpen(true);
  }, [configuration]);

  const loginButtonTarget =
    iframe && configuration?.ssoSetting.forceJwtAuth
      ? { target: "_parent" }
      : {};

  const loginButtonHref = configuration?.ssoSetting.forceJwtAuth
    ? { href: configuration.ssoSetting.signInRedirectUrl }
    : {};

  const loginButtonAttributes: ComponentProps<typeof ThemeButton> = {
    startIcon: <Icon>lock</Icon>,
    variant: "contained",
    ...loginButtonHref,
    ...loginButtonTarget,
  };

  return (
    <>
      <OnlyDesktop>
        <StyledButton
          {...loginButtonAttributes}
          onClick={loginToClaimOnClickDesktop}
        >
          {children}
        </StyledButton>
      </OnlyDesktop>
      <OnlyMobile>
        <StyledButton
          {...loginButtonAttributes}
          onClick={loginToClaimOnClickMobile}
          ref={(node) => {
            mobileAnchorRef.current = node;
          }}
        >
          {children}
        </StyledButton>
        {mobileOpen && (
          <ProfileAccessModal
            defaultAction="signIn"
            itemSlug={itemSlug}
            anchorEl={mobileAnchorRef.current}
            onClose={() => setMobileOpen(false)}
            desktop={false}
          />
        )}
      </OnlyMobile>
    </>
  );
};

export default LoginAnchorButton;
